import type { FullResult } from '@playwright/test/reporter';
import type { RunMetadata } from '../types/index.js';
import type { Logger } from '../utils/logger.js';
import { colors, symbols, box, pad, fit, formatDuration } from '../utils/ui.js';
import { buildUpdateNoticeLines } from '../utils/update-notifier.js';

// Summary box dimensions
const BOX_WIDTH = 72;
const LABEL_WIDTH = 11;

export interface ReporterLogOptions {
  debug: boolean;
}

export interface RunSummaryData {
  runId: string;
  runMetadata: RunMetadata | null;
  testCounts: {
    passed: number;
    failed: number;
    skipped: number;
    timedOut: number;
    interrupted: number;
    flaky: number;
    retried: number;
  };
  artifactCounts: {
    screenshots: number;
    videos: number;
    traces: number;
    other: number;
    unavailable: number;
  };
  artifactsDisabled?: boolean;
  totalTests: number;
  workerCount: number;
  projectNames: Set<string>;
  shardInfo?: { current: number; total: number };
  runUrl?: string;
}

type BoxRow = (content: string) => string;
type BoxLabel = (text: string) => string;

function createBoxHelpers(): {
  row: BoxRow;
  label: BoxLabel;
  topBorder: string;
  bottomBorder: string;
  divider: string;
} {
  const topBorder = `  ${colors.dim(`\u250C${'\u2500'.repeat(BOX_WIDTH)}\u2510`)}`;
  const bottomBorder = `  ${colors.dim(`\u2514${'\u2500'.repeat(BOX_WIDTH)}\u2518`)}`;
  const divider = `  ${colors.dim(`\u251C${'\u2500'.repeat(BOX_WIDTH)}\u2524`)}`;
  const row: BoxRow = (content) => `  ${colors.dim('\u2502')} ${fit(content, BOX_WIDTH - 1)}${colors.dim('\u2502')}`;
  const label: BoxLabel = (text) => colors.dim(pad(text, LABEL_WIDTH));
  return { row, label, topBorder, bottomBorder, divider };
}

function printArtifactsRow(
  row: BoxRow,
  label: BoxLabel,
  counts: RunSummaryData['artifactCounts'],
  isSharded: boolean,
  artifactsDisabled?: boolean
): void {
  // Dim, not the warning-yellow "unavailable" row: an opt-out is not a failure.
  if (artifactsDisabled) {
    console.log(row(`${label('Artifacts')}${colors.dim('not configured (artifacts: false)')}`));
    return;
  }

  const uploaded = counts.screenshots + counts.videos + counts.traces + counts.other;
  if (uploaded === 0 && counts.unavailable === 0) return;

  const parts: string[] = [];
  if (counts.screenshots > 0) parts.push(`${counts.screenshots} screenshots`);
  if (counts.videos > 0) parts.push(`${counts.videos} videos`);
  if (counts.traces > 0) parts.push(`${counts.traces} traces`);
  if (counts.other > 0) parts.push(`${counts.other} other`);

  const summary = parts.length > 0 ? parts.join(colors.dim(' · ')) : colors.dim('none uploaded');
  const failed = counts.unavailable > 0 ? `  ${colors.warning(`${counts.unavailable} unavailable`)}` : '';
  // Each shard reports its own totals; without this the row reads as whole-run.
  const scope = isSharded ? `  ${colors.dim('(this shard)')}` : '';
  console.log(row(`${label('Artifacts')}${summary}${failed}${scope}`));
}

function printResultsSection(row: BoxRow, label: BoxLabel, result: FullResult, data: RunSummaryData): void {
  const statusColor =
    result.status === 'passed' ? colors.success : result.status === 'failed' ? colors.error : colors.warning;
  const statusLabel = result.status === 'timedout' ? 'timed out' : result.status;
  console.log(
    row(`${label('Status')}${statusColor(statusLabel.toUpperCase())}  ${colors.dim(formatDuration(result.duration))}`)
  );

  const counts: string[] = [];
  if (data.testCounts.passed > 0) counts.push(colors.success(`${data.testCounts.passed} passed`));
  if (data.testCounts.failed > 0) counts.push(colors.error(`${data.testCounts.failed} failed`));
  if (data.testCounts.flaky > 0) counts.push(colors.warning(`${data.testCounts.flaky} flaky`));
  if (data.testCounts.skipped > 0) counts.push(colors.dim(`${data.testCounts.skipped} skipped`));
  if (data.testCounts.timedOut > 0) counts.push(colors.error(`${data.testCounts.timedOut} timed out`));
  if (data.testCounts.interrupted > 0) counts.push(colors.warning(`${data.testCounts.interrupted} interrupted`));
  const retriedStr = data.testCounts.retried > 0 ? `  ${colors.dim(`(${data.testCounts.retried} retries)`)}` : '';
  console.log(
    row(
      `${label('Results')}${counts.join(colors.dim(' \u00B7 '))}  ${colors.dim(`of ${data.totalTests}`)}${retriedStr}`
    )
  );
}

function printRunSummary(result: FullResult, data: RunSummaryData): void {
  const { row, label, topBorder, bottomBorder, divider } = createBoxHelpers();

  console.log('');
  console.log(topBorder);
  console.log(row(colors.bold('TestDino Run Summary')));
  console.log(divider);

  // Run & Git
  console.log(row(`${label('Run')}${data.runId}`));
  const git = data.runMetadata?.git;
  if (git?.branch || git?.commit?.hash) {
    const branch = git.branch ? colors.info(git.branch) : '';
    const sha = git.commit?.hash ? ` ${colors.dim('@')} ${colors.dim(git.commit.hash.slice(0, 7))}` : '';
    const msg = git.commit?.message ? `  ${colors.dim(git.commit.message.split('\n')[0].slice(0, 40))}` : '';
    console.log(row(`${label('Git')}${branch}${sha}${msg}`));
  }

  console.log(divider);
  printResultsSection(row, label, result, data);
  console.log(divider);

  // Workers, Shard, Projects
  const shardStr = data.shardInfo ? `${data.shardInfo.current}/${data.shardInfo.total}` : '\u2014';
  console.log(
    row(
      `${label('Workers')}${pad(String(data.workerCount > 0 ? data.workerCount : '\u2014'), 14)}` +
        `${colors.dim(pad('Shard', 8))}${shardStr}`
    )
  );
  console.log(
    row(`${label('Projects')}${data.projectNames.size > 0 ? Array.from(data.projectNames).join(', ') : '\u2014'}`)
  );

  printArtifactsRow(row, label, data.artifactCounts, Boolean(data.shardInfo), data.artifactsDisabled);

  console.log(bottomBorder);

  // Outside the box: `fit` would truncate a run URL past BOX_WIDTH.
  if (data.runUrl) {
    console.log('');
    console.log(`  ${colors.dim('View run')}  ${data.runUrl}`);
  }

  console.log('');
}

const RUN_LINK_FALLBACK_WIDTH = 100;

// Rules, not a box: nothing sits right of the URL, so it is never split —
// terminals only linkify an unbroken string.
function printRunLinkBox(url: string): void {
  const width = process.stdout.columns ?? RUN_LINK_FALLBACK_WIDTH;
  // Indent plus a spare column: filling the width exactly makes it wrap.
  const rule = colors.info(symbols.line.repeat(Math.max(1, width - 4)));
  console.log('');
  console.log(`  ${rule}`);
  console.log('');
  console.log(`  ${colors.blue(symbols.info)} ${colors.bold('See live results')}`);
  console.log('');
  console.log(`  ${colors.url(url)}`);
  console.log('');
  console.log(`  ${rule}`);
  console.log('');
}

function printUpdateNotice(currentVersion: string, latestVersion: string): void {
  console.log(box(buildUpdateNoticeLines(currentVersion, latestVersion), { borderColor: 'white' }));
  console.log('');
}

export function createReporterLog(options: ReporterLogOptions): ReporterLog {
  return {
    success: (msg: string) => console.log(`  ${colors.success(symbols.success)} ${msg}`),
    warn: (msg: string) => console.warn(`  ${colors.warning(symbols.warning)} ${colors.warning(msg)}`),
    error: (msg: string) => console.error(`  ${colors.error(symbols.error)} ${colors.error(msg)}`),
    info: (msg: string) => console.log(`  ${colors.info(symbols.info)} ${msg}`),
    debug: (msg: string) => {
      if (options.debug) {
        console.log(`  ${colors.dim(symbols.debug)} ${colors.dim(msg)}`);
      }
    },
    runLink: (url: string) => printRunLinkBox(url),
    printRunSummary,
    printUpdateNotice,
  };
}

export type ReporterLog = Logger & {
  runLink(url: string): void;
  printRunSummary(result: FullResult, data: RunSummaryData): void;
  printUpdateNotice(currentVersion: string, latestVersion: string): void;
};
